import { useQuery } from '@tanstack/react-query';

// Refresh-on-open (2026-10-21), shared by the staff inbox and the client portal
// inbox. Replies made natively on Facebook / Instagram reach us late, so when a
// thread is OPENED we ask the server to pull that one conversation / post live and
// then re-read the thread. It never blocks: the stored thread renders first, and
// this runs beside it.
//
// ⚠️ A useQuery, not an effect: react-query dedupes concurrent opens and the 60s
// staleTime mirrors the server's own per-thread throttle, so flicking between
// threads cannot hammer the endpoint. A failure is swallowed (retry off, and the
// queryFn never throws) — the stored thread is already on screen.
// Not an AI call: opening a thread bills nothing, unlike the on-demand drafters.

export const LIVE_REFRESH_STALE_MS = 60_000;

export interface LiveRefreshOutcome {
  refreshed: boolean;
  throttled: boolean;
  messagesUpserted: number;
}

export function useLiveRefreshOnOpen(opts: {
  scope: 'staff' | 'portal';
  threadId: string;
  enabled: boolean;
  refresh: (threadId: string) => Promise<LiveRefreshOutcome>;
  /** Called only when new messages actually landed. */
  onNewMessages: () => void;
}): void {
  const { scope, threadId, enabled, refresh, onNewMessages } = opts;
  useQuery({
    queryKey: ['thread-live-refresh', scope, threadId],
    enabled,
    staleTime: LIVE_REFRESH_STALE_MS,
    gcTime: LIVE_REFRESH_STALE_MS,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<LiveRefreshOutcome> => {
      try {
        const outcome = await refresh(threadId);
        if (outcome.messagesUpserted > 0) onNewMessages();
        return outcome;
      } catch {
        return { refreshed: false, throttled: false, messagesUpserted: 0 };
      }
    },
  });
}
